// Live's product preview (`products-list/components/modals/preview-modal`, capture
// design-ref/enlarge-25/08) in the shared lightbox (issue 828, bug 25; its arrows and strip are
// Daniil's, beyond live's): the maker and the day in the header, Edit and Delete for who manages
// the brand (live shows them to all; A3), View of the read-only page for the rest, the main
// image, and beside it the name, the description, "Other images" and "Colors".
import { ActionIcon, ColorSwatch, Group, Text, Tooltip } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { Eye, Pencil, Trash2 } from "lucide-react";

import { useListMembers } from "../../api/generated/endpoints/teams/teams";
import type { Product } from "../../api/generated/model";
import { memberName } from "../../platform/members";
import { MediaPreview, type PreviewItem } from "../../platform/media/MediaPreview";
import { PreviewMaker } from "../../platform/media/PreviewMaker";
import { ThumbImage } from "../../ui/ThumbImage";
import { otherImagesOf, previewImageOf } from "./product-image";
import classes from "./ProductPreview.module.css";

/** A product as the preview shows it: its leading image's file large, and its thumbnail in the
 *  strip and standing in, at the file's size, while the file loads. The strip once drew every
 *  product's original, 46.7 MB for one open on dev (spec ui-perf/product-images). */
function productItem(product: Product): PreviewItem {
  const lead = previewImageOf(product);
  const url = lead?.url ?? null;
  return {
    id: product.id,
    name: product.name,
    kind: "image",
    url,
    thumb: url === null ? null : (lead?.thumb_url ?? url),
    poster: null,
    durationMs: null,
    width: lead?.width ?? null,
    height: lead?.height ?? null,
  };
}

export interface ProductPreviewProps {
  teamId: string;
  brandId: string;
  /** The products the list shows, in its order: what the arrows step through. */
  products: readonly Product[];
  current: string | null;
  onShow: (id: string | null) => void;
  /** The member manages the brand (A3: `manage_brands`): Edit and Delete show, else View. */
  canManage: boolean;
  onDelete: (product: Product) => void;
  /** The delete's question is open over the preview. */
  paused: boolean;
}

export function ProductPreview(props: ProductPreviewProps) {
  const { teamId, brandId, products } = props;
  // Who made each product, read once the preview opens, from the cache where it holds them.
  const members = useListMembers(teamId, {
    query: { enabled: props.current !== null, staleTime: Number.POSITIVE_INFINITY },
  }).data;
  const productOf = (id: string) => products.find((each) => each.id === id);
  return (
    <MediaPreview
      items={products.map(productItem)}
      current={props.current}
      onShow={props.onShow}
      paused={props.paused}
      detailsOpen
      header={(item) => {
        const product = productOf(item.id);
        if (product === undefined) return null;
        const maker = memberName(members?.find((member) => member.user_id === product.created_by));
        return <PreviewMaker name={maker} at={product.created_at} />;
      }}
      actions={(item) => {
        const product = productOf(item.id);
        if (product === undefined) return null;
        // The product's page: its form for who manages the brand, else read-only.
        const page = props.canManage ? "Edit" : "View";
        const open = (
          <Tooltip label={page}>
            <ActionIcon
              variant="subtle"
              color="gray"
              c="var(--cai-color-viewer-text)"
              aria-label={`${page} ${product.name}`}
              renderRoot={(root) => (
                <Link
                  {...root}
                  to="/teams/$teamId/brands/$brandId/products/$productId"
                  params={{ teamId, brandId, productId: product.id }}
                />
              )}
            >
              {props.canManage ? <Pencil size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
            </ActionIcon>
          </Tooltip>
        );
        if (!props.canManage) return open;
        return (
          <>
            {open}
            <Tooltip label="Delete">
              <ActionIcon
                variant="subtle"
                color="gray"
                c="var(--cai-color-viewer-text)"
                aria-label={`Delete ${product.name}`}
                onClick={() => props.onDelete(product)}
              >
                <Trash2 size={18} aria-hidden />
              </ActionIcon>
            </Tooltip>
          </>
        );
      }}
      details={(item) => {
        const product = productOf(item.id);
        return product === undefined ? null : <ProductFacts product={product} />;
      }}
    />
  );
}

/** Live's right pane: the name, the description, the other images and the colors. */
function ProductFacts({ product }: { product: Product }) {
  const others = otherImagesOf(product).filter((image) => image.url !== null);
  return (
    <div className={classes.facts}>
      <Text className={classes.name}>{product.name}</Text>
      {product.description === null ? null : <Text className={classes.text}>{product.description}</Text>}
      <Text className={classes.label}>Other images</Text>
      <div className={classes.images}>
        {others.map((image) => (
          <ThumbImage key={image.asset_id} thumb={image.thumb_url} full={image.url!} alt="" className={classes.image} />
        ))}
      </div>
      {product.colors.length === 0 ? null : (
        <>
          <Text className={classes.label}>Colors</Text>
          <Group gap={8}>
            {product.colors.map((color, at) => (
              <ColorSwatch
                key={`${color.hex}-${at}`}
                color={color.hex}
                size={40}
                radius={4}
                aria-label={color.label ?? color.hex}
              />
            ))}
          </Group>
        </>
      )}
    </div>
  );
}
